// src/components/UtilitiesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';

/**
 * UtilitiesPanel — Water Consumption / Electricity Consumption, driven off the
 * generated massing. One component, `kind` selects which half it shows.
 *
 * The design rule here is provenance, not polish: every number is badged with
 * where it came from, because these three things are NOT equivalent and the
 * architect must be able to tell them apart at a glance —
 *
 *   DCR        the regulation's own figure, with document/page/table. Trustable.
 *   ASSUMED    VAUOM's default because the regulations are silent (occupancy).
 *   UNVERIFIED a placeholder benchmark, not from any document in the corpus.
 *
 * Water is genuinely code-driven. Electricity mostly is not — the codes give a
 * fixture-count method and a solar obligation, no area-based consumption model —
 * and the panel says so rather than quietly presenting a confident figure.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  surfaceHi: 'var(--surface-3,#212a3e)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  textDim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent,#5b9dff)',
  ok: 'var(--ok,#3ecf8e)',
  warn: 'var(--warn,#f0b429)',
  err: 'var(--err,#f0616d)',
};

const SOURCE_BADGE = {
  dcr:        { label: 'DCR',        color: T.ok,      title: 'From the regulation — see citation' },
  user:       { label: 'YOURS',      color: T.accent,  title: 'You entered this value' },
  assumption: { label: 'ASSUMED',    color: T.warn,    title: 'VAUOM default — the regulations do not cover this' },
  benchmark:  { label: 'UNVERIFIED', color: T.err,     title: 'Placeholder benchmark, not from any source document' },
};

const fmtL = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)} kL` : `${n} L`);
const range = (lo, hi, fmt) => (lo === hi ? fmt(lo) : `${fmt(lo)} – ${fmt(hi)}`);

export default function UtilitiesPanel({ project, apiBase, kind = 'water' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [massing, setMassing] = useState(null);
  const [ov, setOv] = useState({});          // user overrides
  const [showOv, setShowOv] = useState(false);

  // seed from the last massing build (same bridge Compliance uses)
  useEffect(() => {
    const cached = project?.cachedCompliance;
    const seed = (detail) => {
      const f = detail?.facts || {};
      const floors = f.floors, area = f.plot_area_m2;
      const gc = detail?.achieved?.exterior?.ground_coverage?.value;
      const footprint = gc != null && area ? (gc / 100) * area : null;
      setMassing({
        builtUpAreaM2: footprint && floors ? footprint * floors : null,
        plotAreaM2: area ?? null,
        use: f.use || 'residential_apartment',
        floors,
      });
    };
    if (cached) seed(cached);
    const onInputs = (e) => seed(e.detail);
    window.addEventListener('complianceInputsChanged', onInputs);
    return () => window.removeEventListener('complianceInputsChanged', onInputs);
  }, [project?.id]);

  const run = useCallback(async () => {
    if (!massing?.builtUpAreaM2) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${apiBase}/api/utilities/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...massing, overrides: ov }),
      });
      const j = await res.json();
      if (j.ok) setData(j); else setErr(j.error || 'Estimate failed');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [apiBase, massing, ov]);

  useEffect(() => { run(); }, [run]);

  if (!massing?.builtUpAreaM2) {
    return (
      <div style={styles.empty}>
        Build the massing first (left panel → Build). Water and electricity are
        estimated from the built-up area it generates.
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.inputsBar}>
        <span><b>{Math.round(massing.builtUpAreaM2).toLocaleString()}</b> m² built-up</span>
        <span style={{ color: T.textDim }}>· {massing.floors} floors · {massing.use.replace(/_/g, ' ')}</span>
        {busy && <span style={{ marginLeft: 'auto', color: T.textDim }}>calculating…</span>}
      </div>

      {err && <div style={{ ...styles.notice, color: T.err, border: `1px solid ${T.err}` }}>{err}</div>}

      {data && kind === 'water' && <WaterView data={data} ov={ov} setOv={setOv} showOv={showOv} setShowOv={setShowOv} />}
      {data && kind === 'electricity' && <ElectricityView data={data} ov={ov} setOv={setOv} />}

      {data && <div style={styles.disclaimer}>{data.disclaimer}</div>}
    </div>
  );
}

function Badge({ source, verified }) {
  const b = SOURCE_BADGE[verified === false && source === 'benchmark' ? 'benchmark' : source];
  if (!b) return null;
  return <span style={{ ...styles.badge, color: b.color, border: `1px solid ${b.color}` }} title={b.title}>{b.label}</span>;
}

function Cite({ cite }) {
  if (!cite) return null;
  return (
    <span style={styles.cite}>
      {cite.code} {cite.version} · p.{cite.page}{cite.table ? ` · ${cite.table}` : ''}{cite.clause ? ` · ${cite.clause}` : ''}
    </span>
  );
}

function WaterView({ data, ov, setOv, showOv, setShowOv }) {
  const { water, occupancy } = data;
  const total = water.lines[0];
  const annualKL = total ? ((total.lowLitresPerDay + total.highLitresPerDay) / 2 * 365) / 1000 : 0;

  return (
    <>
      {/* occupancy — the assumption everything downstream rests on */}
      <div style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitle}>Occupancy</span>
          <Badge source={occupancy.source} />
          <button type="button" style={styles.link} onClick={() => setShowOv((s) => !s)}>
            {showOv ? 'hide' : 'override'}
          </button>
        </div>
        <div style={styles.big}>{occupancy.occupants.toLocaleString()} <span style={styles.unit}>persons</span></div>
        <div style={styles.basis}>{occupancy.basis}</div>
        {occupancy.note && <div style={{ ...styles.notice, color: T.warn, border: `1px solid ${T.warn}` }}>{occupancy.note}</div>}

        {showOv && (
          <div style={styles.ovGrid}>
            {occupancy.dwellingUnits != null && (
              <>
                <label style={styles.ovLabel}>Dwelling units</label>
                <input style={styles.ovInput} type="number" placeholder={occupancy.dwellingUnits}
                  value={ov.dwellingUnits ?? ''} onChange={(e) => setOv({ ...ov, dwellingUnits: e.target.value || undefined })} />
                <label style={styles.ovLabel}>Persons / unit</label>
                <input style={styles.ovInput} type="number" step="0.1" placeholder={occupancy.inputs?.personsPerDwellingUnit}
                  value={ov.personsPerDwellingUnit ?? ''} onChange={(e) => setOv({ ...ov, personsPerDwellingUnit: e.target.value || undefined })} />
              </>
            )}
            <label style={styles.ovLabel}>Total occupants</label>
            <input style={styles.ovInput} type="number" placeholder={occupancy.occupants}
              value={ov.occupants ?? ''} onChange={(e) => setOv({ ...ov, occupants: e.target.value || undefined })} />
          </div>
        )}
      </div>

      {/* demand */}
      <div style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitle}>Daily demand</span>
          <Badge source="dcr" />
        </div>
        {water.lines.map((l) => (
          <div key={l.id} style={styles.row}>
            <div style={styles.rowTop}>
              <span style={l.id.startsWith('of which') || l.label.startsWith('of which') ? styles.rowLabelSub : styles.rowLabel}>
                {l.label}
              </span>
              <span style={styles.rowValue}>{range(l.lowLitresPerDay, l.highLitresPerDay, fmtL)}<span style={styles.unit}>/day</span></span>
            </div>
            <div style={styles.rowSub}>{l.basis} <Cite cite={l.cite} /></div>
          </div>
        ))}
        <div style={styles.annual}>
          ≈ {Math.round(annualKL).toLocaleString()} kL/year
          <span style={styles.basis}> (midpoint × 365)</span>
        </div>
      </div>

      {/* band selector for residential */}
      {water.bands && (
        <div style={styles.section}>
          <div style={styles.sectionHead}><span style={styles.sectionTitle}>Supply band</span><Badge source="dcr" /></div>
          <div style={styles.basis}>Table 48 keys on the <b>community's</b> population, not this building's.</div>
          <select style={styles.select} value={ov.residentialBandId ?? water.band}
            onChange={(e) => setOv({ ...ov, residentialBandId: e.target.value })}>
            {water.bands.map((b) => <option key={b.id} value={b.id}>{b.label} — {b.low}{b.high !== b.low ? `-${b.high}` : ''} lphd</option>)}
          </select>
        </div>
      )}

      {water.types && (
        <div style={styles.section}>
          <div style={styles.sectionHead}><span style={styles.sectionTitle}>Building type</span><Badge source="dcr" /></div>
          <select style={styles.select} value={ov.waterType ?? water.typeId}
            onChange={(e) => setOv({ ...ov, waterType: e.target.value })}>
            {water.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

function ElectricityView({ data, ov, setOv }) {
  const { electricity } = data;
  return (
    <>
      <div style={{ ...styles.notice, color: T.err, border: `1px solid ${T.err}` }}>
        <b>The regulations do not estimate electricity consumption.</b> {electricity.caveat}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitle}>Connected load — enter the real figure</span>
        </div>
        <div style={styles.basis}>
          Everything below, including the statutory solar requirement, is keyed on this number.
          Entering your MEP consultant's value replaces the placeholder.
        </div>
        <input style={{ ...styles.ovInput, width: 160 }} type="number" placeholder="kW"
          value={ov.connectedLoadKW ?? ''} onChange={(e) => setOv({ ...ov, connectedLoadKW: e.target.value || undefined })} />
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHead}><span style={styles.sectionTitle}>Estimated load & energy</span></div>
        {electricity.lines.map((l) => (
          <div key={l.id} style={styles.row}>
            <div style={styles.rowTop}>
              <span style={styles.rowLabel}>{l.label} <Badge source={l.source} verified={l.verified} /></span>
              <span style={styles.rowValue}>{range(l.low, l.high, (n) => n.toLocaleString())}<span style={styles.unit}>{l.unit}</span></span>
            </div>
            <div style={styles.rowSub}>{l.basis}</div>
            {l.note && <div style={styles.warnNote}>{l.note}</div>}
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitle}>Rooftop solar requirement</span><Badge source="dcr" />
        </div>
        {electricity.solar.length === 0 ? (
          <div style={styles.basis}>No Table 99 row applies to this use and plot size.</div>
        ) : (
          electricity.solar.map((s) => (
            <div key={s.id} style={styles.row}>
              <div style={styles.rowTop}>
                <span style={styles.rowLabel}>{s.label}</span>
                <span style={styles.rowValue}>{range(s.low, s.high, (n) => n.toLocaleString())}<span style={styles.unit}>{s.unit}</span></span>
              </div>
              <div style={styles.rowSub}>{s.basis} <Cite cite={s.cite} /></div>
              {s.note && <div style={styles.warnNote}>{s.note}</div>}
            </div>
          ))
        )}
        {electricity.solarNotes?.map((n, i) => <div key={i} style={styles.basis}>· {n}</div>)}
      </div>

      <details style={styles.details}>
        <summary style={styles.summary}>
          NBC point ratings <Badge source="dcr" /> — the code's own load method
        </summary>
        <div style={styles.basis}>
          {electricity.pointRatings.note} <Cite cite={electricity.pointRatings.cite} />
        </div>
        <div style={styles.pointGrid}>
          {electricity.pointRatings.points.map((p) => (
            <React.Fragment key={p.id}>
              <span style={styles.pointLabel}>{p.label}</span>
              <span style={styles.pointVal}>{p.watts.toLocaleString()} W</span>
            </React.Fragment>
          ))}
        </div>
      </details>
    </>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, color: T.text },
  empty: { fontSize: 12, color: T.textDim, lineHeight: 1.5 },
  inputsBar: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: T.text, paddingBottom: 2 },
  section: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 5 },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 6 },
  sectionTitle: { fontSize: 12, fontWeight: 600 },
  big: { fontSize: 20, fontWeight: 600, lineHeight: 1.1 },
  unit: { fontSize: 10, color: T.textDim, marginLeft: 4, fontWeight: 400 },
  basis: { fontSize: 10.5, color: T.textDim, lineHeight: 1.45 },
  warnNote: { fontSize: 10, color: T.warn, lineHeight: 1.4 },
  row: { display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 5, borderTop: `1px solid rgba(58,68,86,0.35)` },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  rowLabel: { fontSize: 11.5 },
  rowLabelSub: { fontSize: 11, color: T.textDim, paddingLeft: 10 },
  rowValue: { fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  rowSub: { fontSize: 10, color: T.textDim },
  annual: { fontSize: 12, fontWeight: 600, paddingTop: 6, borderTop: `1px solid ${T.border}`, marginTop: 2 },
  badge: { fontSize: 8, fontWeight: 700, letterSpacing: 0.3, border: '1px solid', borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap' },
  cite: { fontSize: 9.5, color: T.accent, marginLeft: 4 },
  notice: { fontSize: 10.5, border: '1px solid', borderRadius: 6, padding: '6px 8px', background: 'color-mix(in srgb, var(--warn) 10%, transparent)', lineHeight: 1.45 },
  disclaimer: { fontSize: 9.5, color: T.textDim, fontStyle: 'italic', lineHeight: 1.4 },
  link: { marginLeft: 'auto', fontSize: 10, background: 'none', border: 'none', color: T.accent, cursor: 'pointer', padding: 0 },
  ovGrid: { display: 'grid', gridTemplateColumns: 'auto 90px', gap: 5, alignItems: 'center', marginTop: 4 },
  ovLabel: { fontSize: 10.5, color: T.textDim },
  ovInput: { fontSize: 11, padding: '4px 7px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceHi, color: T.text, outline: 'none', width: '100%' },
  select: { fontSize: 11, padding: '5px 7px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceHi, color: T.text, outline: 'none' },
  details: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10 },
  summary: { fontSize: 11.5, fontWeight: 600, cursor: 'pointer' },
  pointGrid: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '3px 10px', marginTop: 7, fontSize: 10.5 },
  pointLabel: { color: T.textDim },
  pointVal: { textAlign: 'right', fontWeight: 600 },
};
